import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import AnimeCard from "@/components/feature/AnimeCard";
import NewsCard from "@/components/feature/NewsCard";
import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import { formatCount, formatDate } from "@/utils/format";
import HeroCarousel from "./components/HeroCarousel";
import NewsQuickLinks from "./components/NewsQuickLinks";
import RankingList from "./components/RankingList";

const headlines = newsList.filter((n) => n.isHeadline).slice(0, 3);
const featuredAnime = [...animeList].sort((a, b) => b.heat - a.heat).slice(0, 8);
const latestNews = [...newsList]
  .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
  .slice(0, 6);
const editorPicks = [...animeList].sort((a, b) => b.rating - a.rating).slice(0, 5);

export default function Home() {
  return (
    <SiteLayout
      title="91动漫-最新动漫资讯与精彩动漫推荐平台"
      description="91动漫为您提供丰富的动漫资讯、动画片推荐和动漫作品介绍，涵盖国内外热门动漫，助您掌握最新动态，体验多样动漫乐趣。"
      keywords="91动漫,动漫资讯,动画推荐,热门动漫,动漫作品,动漫平台,动漫动态"
    >

      <HeroCarousel items={headlines} />

      {/* 数据条 */}
      <section className="w-full bg-foreground-950 border-t border-background-50/10">
        <div className="w-full px-4 md:px-8 py-4 md:py-5 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {[
            { label: "收录作品", value: `${animeList.length}`, unit: "部", icon: "ri-book-2-line" },
            { label: "资讯条目", value: `${newsList.length}`, unit: "条", icon: "ri-newspaper-line" },
            { label: "今日更新", value: "12", unit: "篇", icon: "ri-refresh-line" },
            { label: "本周热度", value: "98.6", unit: "万", icon: "ri-fire-line" },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-md bg-primary-500/15 text-primary-400 flex items-center justify-center shrink-0">
                <i className={`${stat.icon} text-base`} />
              </span>
              <div>
                <p className="font-heading font-bold text-background-50 text-base md:text-lg leading-none">
                  {stat.value}
                  <span className="ml-0.5 text-[11px] font-normal text-background-400">{stat.unit}</span>
                </p>
                <p className="mt-1 text-[11px] text-background-400">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 资讯快捷入口 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
        <SectionHeading
          eyebrow="News Channels"
          title="按资讯分类快速浏览"
          desc="新番、行业、剧场版、活动与声优，选一个你关心的方向直接进入。"
          action={{ to: "/news", label: "查看全部资讯" }}
        />
        <NewsQuickLinks />
      </section>

      {/* 热门推荐 + 排行榜 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100 border-y border-background-200">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-10">
          <div>
            <SectionHeading
              eyebrow="Popular Anime"
              title="热门动漫推荐"
              desc="结合近 7 日播放讨论热度与编辑评分综合排序。"
              action={{ to: "/anime", label: "全部作品" }}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5" data-product-shop>
              {featuredAnime.map((anime, idx) => (
                <AnimeCard key={anime.id} anime={anime} index={idx} />
              ))}
            </div>
          </div>
          <aside className="flex flex-col gap-5">
            <RankingList items={editorPicks} />
            <div className="p-5 rounded-lg bg-foreground-950 text-background-100">
              <h3 className="font-heading font-semibold text-background-50 text-sm flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-accent-400">
                  <i className="ri-sparkling-2-line text-base" />
                </span>
                编辑周选
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-background-300">
                本周我们最想推荐的是《星海列车》第 18 话。它用一列车厢的沉默，讲完了一整段告别。
              </p>
              <Link
                to="/anime/detail/a01"
                className="mt-4 inline-flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 transition-colors duration-200 cursor-pointer"
              >
                去看看
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* 最新资讯 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
        <SectionHeading
          eyebrow="Latest News"
          title="最新动漫资讯"
          desc="每日两次更新，追踪国内外动漫动态。"
          action={{ to: "/news", label: "更多资讯" }}
        />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-10">
          <div className="flex flex-col gap-3 md:gap-4">
            {latestNews.map((item, idx) => (
              <NewsCard key={item.id} news={item} index={idx} />
            ))}
          </div>
          <aside>
            <div className="bg-background-100 rounded-lg border border-background-200 p-4 md:p-5">
              <h3 className="font-heading font-semibold text-foreground-950 text-sm flex items-center gap-2 mb-3">
                <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                  <i className="ri-list-check-2 text-base" />
                </span>
                今日速览
              </h3>
              <ul className="space-y-3">
                {newsList.slice(0, 5).map((item, idx) => (
                  <li key={item.id}>
                    <Link
                      to={`/news/detail/${item.id}`}
                      className="flex gap-2.5 group cursor-pointer"
                    >
                      <span className="text-[11px] font-label text-foreground-400 pt-0.5 w-4 shrink-0">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <p className="text-xs md:text-sm text-foreground-800 leading-relaxed line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {item.title}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-background-200">
                <Link
                  to="/anime"
                  className="flex items-center justify-center w-full h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer"
                >
                  浏览动漫作品库
                </Link>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[
                { label: "动漫资讯", value: formatCount(128400), icon: "ri-newspaper-line", to: "/news" },
                { label: "作品图鉴", value: `${animeList.length}`, icon: "ri-book-2-line", to: "/anime" },
              ].map((box) => (
                <Link
                  key={box.label}
                  to={box.to}
                  className="p-3.5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors duration-200 cursor-pointer"
                >
                  <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className={`${box.icon} text-base`} />
                  </span>
                  <p className="mt-2 font-heading font-bold text-foreground-950 text-sm">{box.value}</p>
                  <p className="mt-0.5 text-[11px] text-foreground-500">{box.label}</p>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>

      {/* 网站简介 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100 border-t border-background-200">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 lg:gap-14 items-center">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-[2px] rounded-full bg-primary-500" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-primary-600 font-label">About 91 Anime</span>
            </div>
            <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
              关于 91动漫
            </h2>
            <p className="mt-3 text-sm text-foreground-700 leading-[1.9]">
              91动漫是一个专注于动漫资讯与作品推荐的内容门户。我们把每季新番情报、行业动态、剧场版消息与线下活动整理成清晰的信息流，也收录了国内外值得一看的动画作品，用简短的简介、类型标签与编辑推荐，帮你在几分钟内判断一部作品是否对味。
            </p>
            <p className="mt-3 text-sm text-foreground-700 leading-[1.9]">
              本站不提供在线播放与下载，所有内容用于资讯浏览与作品了解。评分为演示数据，作品版权归原权利方所有。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer"
              >
                了解更多
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-600 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer"
              >
                搜索作品
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {[
              { title: "每日更新", desc: "早晚两次同步最新动漫资讯", icon: "ri-time-line" },
              { title: "多维筛选", desc: "按类型、地区、年份快速定位", icon: "ri-filter-3-line" },
              { title: "编辑推荐", desc: "人工筛选值得追的作品清单", icon: "ri-thumb-up-line" },
              { title: "详情图鉴", desc: "角色、制作团队与推荐理由", icon: "ri-profile-line" },
            ].map((item, idx) => (
              <div
                key={item.title}
                className={`p-4 md:p-5 rounded-lg bg-background-50 border border-background-200 animate-fade-up ${
                  idx % 2 === 1 ? "mt-0 md:mt-6" : ""
                }`}
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                  <i className={`${item.icon} text-base`} />
                </span>
                <h3 className="mt-3 font-heading font-semibold text-foreground-950 text-sm">{item.title}</h3>
                <p className="mt-1 text-[11px] md:text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-12 bg-background-50">
        <div className="rounded-lg bg-foreground-950 px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading font-bold text-background-50 text-lg md:text-xl">
              更新时间提示
            </h2>
            <p className="mt-2 text-xs md:text-sm text-background-300 leading-relaxed max-w-xl">
              本页最新资讯更新于 {formatDate(latestNews[0]?.publishedAt ?? "2025-10-17")}，内容与分类页、详情页保持一致。
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer"
            >
              动漫资讯
            </Link>
            <Link
              to="/anime"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-50/25 hover:border-background-50/60 text-background-100 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer"
            >
              动漫作品
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}